import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { MiniGameStudent } from '../../../interfaces/mini-game.interface';

@Component({
  selector: 'app-student-spotlight',
  template: `
    <div class="spotlight-stage" *ngIf="student">
      <!-- SVG mask: white = visible (dimmed), black circle = hole (transparent) -->
      <svg class="spotlight-svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="spotlight-blur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="30"/>
          </filter>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white"/>
            <circle [attr.cx]="cx" [attr.cy]="cy" [attr.r]="radius"
                    fill="black" filter="url(#spotlight-blur)"/>
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.88)"
              mask="url(#spotlight-mask)"
              [class.fade-in]="phase >= 0"/>
      </svg>

      <!-- Student content -->
      <div class="student-reveal" [class.visible]="phase >= 3">
        <div class="avatar-ring">
          <app-student-avatar [student]="student" [size]="120" [active]="true"></app-student-avatar>
        </div>
        <div class="student-name" [class.visible]="phase >= 4">
          {{ currLang === 'ar' ? student.name.ar : student.name.en }}
        </div>
        <div class="student-label" *ngIf="label" [class.visible]="phase >= 4">
          {{ label }}
        </div>
      </div>
    </div>
  `,
  styles: [`
    .spotlight-stage {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 100;
    }

    .spotlight-svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      overflow: visible;
    }

    .spotlight-svg rect.fade-in {
      animation: fadeIn 0.3s ease-out forwards;
    }

    .student-reveal {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      opacity: 0;
      transform: scale(0.5);
    }
    .student-reveal.visible {
      animation: studentPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    .avatar-ring {
      position: relative;
      padding: 8px;
    }

    .student-name {
      color: white;
      font-size: 28px;
      font-weight: 800;
      text-align: center;
      text-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
      opacity: 0;
      transform: translateY(10px);
    }
    .student-name.visible {
      animation: nameSlide 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    .student-label {
      color: rgba(255, 255, 255, 0.85);
      font-size: 18px;
      font-weight: 600;
      text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
      opacity: 0;
      transform: translateY(8px);
    }
    .student-label.visible {
      animation: nameSlide 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s forwards;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes studentPop {
      0% { opacity: 0; transform: scale(0.5); }
      60% { transform: scale(1.06); }
      100% { opacity: 1; transform: scale(1); }
    }

    @keyframes nameSlide {
      0% { opacity: 0; transform: translateY(10px); }
      100% { opacity: 1; transform: translateY(0); }
    }
  `]
})
export class StudentSpotlightComponent implements OnInit, OnDestroy {
  @Input() student: MiniGameStudent | null = null;
  @Input() label = '';
  @Input() currLang: any = 'en';

  phase = 0;
  cx = '110%';
  cy = '-10%';
  radius = '180';
  private resizeListener: any;
  private timers: any[] = [];
  private animFrame: any;

  ngOnInit(): void {
    this.updateRadius();
    this.resizeListener = () => this.updateRadius();
    window.addEventListener('resize', this.resizeListener);

    this.cx = '110%';
    this.cy = '-10%';

    this.timers.push(setTimeout(() => {
      this.phase = 1;
      this.animateCircleToCenter();
    }, 100));

    this.timers.push(setTimeout(() => this.phase = 2, 1000));
    this.timers.push(setTimeout(() => this.phase = 3, 1200));
    this.timers.push(setTimeout(() => this.phase = 4, 1600));
  }

  private animateCircleToCenter(): void {
    const duration = 800;
    const start = performance.now();
    const startX = 110;
    const startY = -10;
    const endX = 50;
    const endY = 50;

    const animate = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);

      this.cx = (startX + (endX - startX) * ease) + '%';
      this.cy = (startY + (endY - startY) * ease) + '%';

      if (t < 1) {
        this.animFrame = requestAnimationFrame(animate);
      }
    };

    this.animFrame = requestAnimationFrame(animate);
  }

  private updateRadius(): void {
    const smaller = Math.min(window.innerWidth, window.innerHeight);
    this.radius = String(Math.round(smaller * 0.25));
  }

  ngOnDestroy(): void {
    this.timers.forEach(t => clearTimeout(t));
    if (this.animFrame) {
      cancelAnimationFrame(this.animFrame);
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
}
